import React, { useState, useEffect, useCallback } from "react";
import { Form } from "react-bootstrap";
import {
  Plus, Pencil, Trash2, ChevronRight, Package, Layers,
} from "lucide-react";
import * as plm from "../../services/plmService";
import EntityFormDialog from "./EntityFormDialog";

const EMPTY_PRODUCT = { name: "", description: "", type: "Product" };
const EMPTY_VERSION = { versionLabel: "", releaseNotes: "", status: "Draft" };

const ProductsTab = ({ token, plm: plmHook, inline, selectedProductId: externalSelectedId }) => {
  const { products, fetchProducts, createProduct, updateProduct, deleteProduct } = plmHook;

  const [showProductDlg, setShowProductDlg] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState(EMPTY_PRODUCT);
  const [saving, setSaving] = useState(false);

  const [showVersionDlg, setShowVersionDlg] = useState(false);
  const [versionForm, setVersionForm] = useState(EMPTY_VERSION);
  const [versionProductId, setVersionProductId] = useState(null);

  const [expandedId, setExpandedId] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);

  const loadVersions = useCallback(async (productId) => {
    setLoadingVersions(true);
    try {
      const data = await plm.fetchVersionsApi({ token, productId });
      setVersions(data);
    } catch { setVersions([]); }
    finally { setLoadingVersions(false); }
  }, [token]);

  useEffect(() => {
    if (expandedId) loadVersions(expandedId);
  }, [expandedId, loadVersions]);

  const openCreate = () => { setEditingProduct(null); setProductForm(EMPTY_PRODUCT); setShowProductDlg(true); };
  const openEdit = (p) => { setEditingProduct(p); setProductForm({ name: p.name, description: p.description || "", type: p.type || "Product" }); setShowProductDlg(true); };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    if (editingProduct) {
      await updateProduct(editingProduct.id, productForm);
    } else {
      await createProduct(productForm);
    }
    setSaving(false);
    setShowProductDlg(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product and all its versions?")) return;
    await deleteProduct(id);
    if (expandedId === id) setExpandedId(null);
  };

  const openVersionCreate = (productId) => {
    setVersionProductId(productId);
    const latest = versions.length ? versions[0] : null;
    setVersionForm({ ...EMPTY_VERSION, basedOnVersionId: latest?.id || "" });
    setShowVersionDlg(true);
  };

  const handleVersionSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { ...versionForm };
    if (!body.basedOnVersionId) delete body.basedOnVersionId;
    await plm.createVersionApi({ token, productId: versionProductId, version: body });
    await loadVersions(versionProductId);
    setSaving(false);
    setShowVersionDlg(false);
  };

  const handleDeleteVersion = async (productId, versionId) => {
    if (!window.confirm("Delete this version?")) return;
    await plm.deleteVersionApi({ token, productId, versionId });
    await loadVersions(productId);
  };

  const selectedProduct = inline ? products.find(p => p.id === externalSelectedId) : null;

  const versionStatusColor = (status) => {
    switch (status) { case "Released": return "green"; case "Deprecated": return "red"; default: return "yellow"; }
  };

  const dialogs = (
    <>
      <EntityFormDialog
        show={showProductDlg}
        onClose={() => setShowProductDlg(false)}
        title={editingProduct ? "Edit Product" : "New Product"}
        onSubmit={handleProductSubmit}
        saving={saving}
      >
        <Form.Group className="mb-3">
          <Form.Label>Name *</Form.Label>
          <Form.Control required value={productForm.name}
            onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Type</Form.Label>
          <Form.Select value={productForm.type}
            onChange={(e) => setProductForm({ ...productForm, type: e.target.value })}>
            <option value="Product">Product</option>
            <option value="Service">Service</option>
          </Form.Select>
        </Form.Group>
        <Form.Group>
          <Form.Label>Description</Form.Label>
          <Form.Control as="textarea" rows={3} value={productForm.description}
            onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} />
        </Form.Group>
      </EntityFormDialog>

      <EntityFormDialog
        show={showVersionDlg}
        onClose={() => setShowVersionDlg(false)}
        title="New Version"
        onSubmit={handleVersionSubmit}
        saving={saving}
      >
        <Form.Group className="mb-3">
          <Form.Label>Version Label *</Form.Label>
          <Form.Control required placeholder="e.g. v1.0" value={versionForm.versionLabel}
            onChange={(e) => setVersionForm({ ...versionForm, versionLabel: e.target.value })} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Status</Form.Label>
          <Form.Select value={versionForm.status}
            onChange={(e) => setVersionForm({ ...versionForm, status: e.target.value })}>
            <option value="Draft">Draft</option>
            <option value="Released">Released</option>
            <option value="Deprecated">Deprecated</option>
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Based On Version</Form.Label>
          <Form.Select value={versionForm.basedOnVersionId}
            onChange={(e) => setVersionForm({ ...versionForm, basedOnVersionId: e.target.value })}>
            <option value="">None (start fresh)</option>
            {versions.map((v) => (
              <option key={v.id} value={v.id}>{v.versionLabel}</option>
            ))}
          </Form.Select>
          <Form.Text className="text-muted">Inherits personas, journeys, and requirements from selected version.</Form.Text>
        </Form.Group>
        <Form.Group>
          <Form.Label>Release Notes</Form.Label>
          <Form.Control as="textarea" rows={2} value={versionForm.releaseNotes}
            onChange={(e) => setVersionForm({ ...versionForm, releaseNotes: e.target.value })} />
        </Form.Group>
      </EntityFormDialog>
    </>
  );

  // Inline mode
  if (inline) {
    return (
      <>
        <div className="d-flex align-items-center gap-1">
          <button className="plm-btn-primary" style={{ padding: "5px 10px", fontSize: "0.8rem" }} onClick={openCreate}>
            <Plus size={14} /> New
          </button>
          {selectedProduct && (
            <>
              <button className="plm-btn-ghost" onClick={() => openEdit(selectedProduct)}><Pencil size={14} /></button>
              <button className="plm-btn-ghost danger" onClick={() => handleDelete(selectedProduct.id)}><Trash2 size={14} /></button>
            </>
          )}
        </div>
        {dialogs}
      </>
    );
  }

  // Full mode
  return (
    <div>
      <div className="plm-section-header">
        <h2 className="plm-section-title">
          <span className="icon-wrap"><Package size={20} /></span>
          Products
        </h2>
        <button className="plm-btn-primary" onClick={openCreate}>
          <Plus size={16} /> New Product
        </button>
      </div>

      {products.length === 0 ? (
        <div className="plm-empty">
          <div className="plm-empty-icon"><Package size={28} /></div>
          <div className="plm-empty-title">No products yet</div>
          <div className="plm-empty-desc">Create your first product to get started.</div>
          <button className="plm-btn-primary" onClick={openCreate}>
            <Plus size={16} /> Create Product
          </button>
        </div>
      ) : (
        <div className="d-flex flex-column gap-2">
          {products.map((p) => (
            <div key={p.id} className="plm-card">
              <div
                className="plm-card-header"
                onClick={() => setExpandedId(expandedId === p.id ? null : p.id)}
              >
                <div className="d-flex align-items-center gap-3">
                  <ChevronRight size={16} className={`plm-chevron ${expandedId === p.id ? "open" : ""}`} />
                  <span style={{ fontWeight: 700 }}>{p.name}</span>
                  <span className="plm-badge gray" style={{ textTransform: "uppercase" }}>{p.type || "Product"}</span>
                </div>
                <div className="d-flex gap-1" onClick={(e) => e.stopPropagation()}>
                  <button className="plm-btn-ghost" onClick={() => openEdit(p)}><Pencil size={14} /></button>
                  <button className="plm-btn-ghost danger" onClick={() => handleDelete(p.id)}><Trash2 size={14} /></button>
                </div>
              </div>

              {expandedId === p.id && (
                <div className="plm-card-body" style={{ paddingTop: 14 }}>
                  {p.description && (
                    <p style={{ fontSize: "0.85rem", color: "var(--plm-text-muted)", marginBottom: 14 }}>{p.description}</p>
                  )}

                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <div className="d-flex align-items-center gap-2" style={{ fontWeight: 600, fontSize: "0.85rem" }}>
                      <Layers size={14} /> Versions
                    </div>
                    <button className="plm-btn-ghost" onClick={() => openVersionCreate(p.id)}>
                      <Plus size={14} /> Add Version
                    </button>
                  </div>

                  {loadingVersions ? (
                    <div style={{ fontSize: "0.8rem", color: "var(--plm-text-muted)" }}>Loading versions...</div>
                  ) : versions.length === 0 ? (
                    <div style={{ fontSize: "0.8rem", color: "var(--plm-text-muted)" }}>No versions yet.</div>
                  ) : (
                    <div className="d-flex flex-column gap-1">
                      {versions.map((v) => (
                        <div key={v.id} className="d-flex justify-content-between align-items-center"
                          style={{ padding: "8px 12px", background: "#f8fafc", borderRadius: 8, fontSize: "0.85rem" }}>
                          <div>
                            <span style={{ fontWeight: 600 }}>{v.versionLabel}</span>
                            <span className={`plm-badge ${versionStatusColor(v.status)}`} style={{ marginLeft: 8 }}>
                              {v.status}
                            </span>
                            {v.releaseNotes && (
                              <div style={{ fontSize: "0.78rem", color: "var(--plm-text-muted)", marginTop: 2 }}>{v.releaseNotes}</div>
                            )}
                          </div>
                          <button className="plm-btn-ghost danger" onClick={() => handleDeleteVersion(p.id, v.id)}>
                            <Trash2 size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {dialogs}
    </div>
  );
};

export default ProductsTab;
